import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SearchBar from '@/components/feature/SearchBar';
import MovieCard from '@/components/feature/MovieCard';
import NewsCard from '@/components/feature/NewsCard';
import { useSeo } from '@/hooks/useSeo';
import { searchMovies, searchNews } from '@/utils/media';

const HOT_SUGGESTIONS = ['科幻', '悬疑', '古装', '都市', '资讯'];

export default function SearchPage() {
  const [params] = useSearchParams();
  const keyword = params.get('q')?.trim() ?? '';

  const movieResults = keyword ? searchMovies(keyword) : [];
  const newsResults = keyword ? searchNews(keyword) : [];
  const total = movieResults.length + newsResults.length;

  useSeo({
    title: keyword ? `“${keyword}”的搜索结果 - 91爱爱` : '搜索影视资源与资讯 - 91爱爱',
    description: keyword
      ? `91爱爱关于“${keyword}”的搜索结果显示：共 ${total} 条相关的影视资源与资讯内容。`
      : '在91爱爱搜索影视资源与影视资讯，支持按名称、类型与关键词匹配。',
    keywords: `91爱爱,搜索,影视搜索,${keyword}`,
  });

  return (
    <SiteLayout>
      <div className="mx-auto max-w-[1280px] px-4 pt-6 md:px-6">
        <Breadcrumb items={[{ label: '搜索结果' }]} />
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6">
        <div className="rounded-lg border border-background-200 bg-primary-50/60 p-5 md:p-6">
          <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {keyword ? (
              <>
                搜索“<span className="text-primary-600">{keyword}</span>”
              </>
            ) : (
              '搜索影视资源与资讯'
            )}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {keyword ? `共找到 ${total} 条相关结果` : '输入关键词，快速查找你想看的影视作品与资讯内容。'}
          </p>
          <div className="mt-4 max-w-2xl">
            <SearchBar size="lg" defaultValue={keyword} />
          </div>
          {!keyword && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">热门搜索：</span>
              {HOT_SUGGESTIONS.map((word) => (
                <Link
                  key={word}
                  to={`/search?q=${encodeURIComponent(word)}`}
                  className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
                >
                  {word}
                </Link>
              ))}
            </div>
          )}
        </div>

        {!keyword ? null : total === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20">
            <i className="ri-search-eye-line text-5xl text-foreground-300" />
            <p className="mt-4 font-heading text-lg font-semibold text-foreground-800">
              没有找到与“{keyword}”相关的结果
            </p>
            <p className="mt-1 text-sm text-foreground-500">试试更换关键词，或浏览以下推荐分类</p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <Link
                to="/category/movie"
                className="flex h-10 items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
              >
                <i className="ri-film-line" />
                浏览电影
              </Link>
              <Link
                to="/category/tv"
                className="flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-live-line" />
                浏览电视剧
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-8 flex flex-col gap-10">
            <section aria-label="影视资源结果">
              <div className="mb-4 flex items-center justify-between border-b border-background-200 pb-3">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-movie-2-line text-primary-600" />
                  影视资源
                  <span className="text-xs font-normal text-foreground-400">
                    （{movieResults.length}）
                  </span>
                </h2>
              </div>
              {movieResults.length > 0 ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                  {movieResults.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <p className="rounded-lg border border-dashed border-background-300 bg-background-50 py-8 text-center text-sm text-foreground-500">
                  暂无匹配的影视资源
                </p>
              )}
            </section>

            <section aria-label="影视资讯结果">
              <div className="mb-4 flex items-center justify-between border-b border-background-200 pb-3">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-newspaper-line text-primary-600" />
                  影视资讯
                  <span className="text-xs font-normal text-foreground-400">
                    （{newsResults.length}）
                  </span>
                </h2>
              </div>
              {newsResults.length > 0 ? (
                <div className="grid gap-3 md:grid-cols-2">
                  {newsResults.map((article) => (
                    <NewsCard key={article.id} article={article} variant="card" />
                  ))}
                </div>
              ) : (
                <p className="rounded-lg border border-dashed border-background-300 bg-background-50 py-8 text-center text-sm text-foreground-500">
                  暂无匹配的资讯内容
                </p>
              )}
            </section>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}